import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { fetchSiteSettings, fetchSiteTabs } from "../lib/api";

const SiteContext = createContext(null);

const FALLBACK = {
  brand_name: "Reness AI",
  logo_url: "/reness-logo.png",
  tagline: "Reness AI builds production-grade automations for ambitious teams.",
  contact: { email: "hello@renessai.com", phone: "+1 (415) 555-0102", address: "San Francisco · Remote" },
  social: { twitter: "", github: "", linkedin: "", facebook: "", instagram: "" },
  hero: {
    eyebrow: "AI Automation Studio · Reness AI",
    headline_top: "I WANT",
    headline_mid: "TO",
    headline_accent: "AUTOMATE",
    subline:
      "We help ambitious teams replace busywork with AI workflows that ship, scale, and pay for themselves.",
  },
  footer_text: "© Reness AI — All systems automated.",
};

const FALLBACK_TABS = [
  { id: "p", label: "Product", path: "/product", order: 1, enabled: true },
  { id: "pr", label: "Pricing", path: "/pricing", order: 2, enabled: true },
  { id: "c", label: "Courses", path: "/courses", order: 3, enabled: true },
  { id: "co", label: "Consult", path: "/consult", order: 4, enabled: true },
];

export const SiteProvider = ({ children }) => {
  const [settings, setSettings] = useState(FALLBACK);
  const [tabs, setTabs] = useState(FALLBACK_TABS);

  const refresh = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([fetchSiteSettings(), fetchSiteTabs()]);
      if (s && s.brand_name) setSettings({ ...FALLBACK, ...s });
      if (t?.tabs?.length) setTabs(t.tabs);
    } catch (_) {
      /* keep fallback */
    }
  }, []);

  useEffect(() => {
    let active = true;
    let timer;

    const load = async () => {
      try {
        const [s, t] = await Promise.all([fetchSiteSettings(), fetchSiteTabs()]);
        if (!active) return;
        if (s && s.brand_name) setSettings({ ...FALLBACK, ...s });
        if (t?.tabs?.length) setTabs(t.tabs);
        timer = setTimeout(load, 30000); // Poll every 30s
      } catch (_) {
        if (!active) return;
        timer = setTimeout(load, 10000); // Retry sleeping backend in 10s
      }
    };

    load();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, []);

  return (
    <SiteContext.Provider value={{ settings, tabs, refresh }}>
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = () => useContext(SiteContext);
export default SiteContext;
